import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom'; 
import { io, Socket } from 'socket.io-client'; 
import { useSelector, useDispatch } from 'react-redux'; 
import { toast } from 'sonner'; 
import { ChatButton } from './ChatButton';
import { CallButton } from './CallButton';
import { VideoButton } from './VideoButton';

import { ConnectivityIntakeModal } from '../globalIntake/ConnectivityIntakeModal';
import { connectivityAPI } from '../../api/connectivityApis/connectivity.api';

import { useConnectivity } from '../../hooks/connectivityHooks/useConnectivity';
import { useCall } from '../../hooks/connectivityHooks/useCall';
import { useVideo } from '../../hooks/connectivityHooks/useVideo';

import type { RootState } from '../../store/store'; 
import { setQueue, clearQueue, setMinimized } from '../../store/slices/queueSlice'; 

// 🟢 IMPORT THE ACTIVE SESSION ACTION
import { setActiveSession } from '../../store/slices/activeSessionSlice';

const SOCKET_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

interface ThreeButtonWrapperProps {
  variant?: 'default' | 'supreme';
  isChatAvailable?: boolean;
  isCallAvailable?: boolean;
  isVideoAvailable?: boolean;
  expertCode: string;
  expertName: string;
  categoryId?: number; // 🟢 NEW: Now accepts categoryId from parent
  expertCategory?: string; 
  expertSubCategories?: string[] | string; 
  expertPhoto?: string;
  liveStatus?: string; 
  isLive?: boolean;
}

export const ThreeButtonWrapper: React.FC<ThreeButtonWrapperProps> = ({ 
  variant = 'default',
  isChatAvailable = true,
  isCallAvailable = true,
  isVideoAvailable = true,
  expertCode,
  expertName,
  categoryId, // 🟢
  expertCategory,
  expertSubCategories,
  expertPhoto,
  liveStatus,
  isLive = false
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { startSession: startChatSession } = useConnectivity();
  const { startCall } = useCall();
  const { startVideo } = useVideo();
  
  const [intakeMode, setIntakeMode] = useState<'chat' | 'call' | 'video' | null>(null);
  const queueSocketRef = useRef<Socket | null>(null);

  const isConnectingRef = useRef(false);

  const { activeQueue, isMinimized } = useSelector((state: RootState) => state.queue);
  const isMyQueue = activeQueue?.expertCode === expertCode;

  const isOffline = !liveStatus || liveStatus === 'offline'; 

  // 🟢 FIX 1: Extract stable primitive values from the activeQueue object
  const activeExpertCode = activeQueue?.expertCode;
  const activeMode = activeQueue?.mode;

  // 🟢 2. Update the hook to only trigger when the structural queue state changes, 
  //    ignoring positional rank changes.
  useEffect(() => {
    if (isMyQueue && activeQueue && !queueSocketRef.current) {
      setupQueueSocket(activeQueue.mode, activeQueue.intakeData);
    }
  }, [isMyQueue, activeExpertCode, activeMode]);

  useEffect(() => {
    return () => {
      if (queueSocketRef.current) {
        queueSocketRef.current.disconnect();
        queueSocketRef.current = null;
      }
    };
  }, []);

  // 🟢 FIXED: Now dynamically extracting the generated categoryId!
  const routeSessionStart = async (mode: 'chat' | 'call' | 'video', intakeData: any) => {
   
    
    // 🟢 Grab the ID we just generated in the modal
     const catId = intakeData?.categoryId || categoryId || null;

    if (mode === 'chat') return await startChatSession(expertCode, mode, catId, intakeData);
    if (mode === 'call') return await startCall(expertCode, catId, intakeData);
    if (mode === 'video') return await startVideo(expertCode, catId, intakeData);
  }

  const setupQueueSocket = (mode: 'chat' | 'call' | 'video', intakeData: any) => {
    const currentUserCode = localStorage.getItem('user_code') || 'unknown';
    

    const queueSocket = io(SOCKET_URL, {
      path: '/socket.io/',
      transports: ['websocket'],
      upgrade: false,
      query: { senderCode: currentUserCode, senderType: 'user' },
      reconnectionAttempts: 5, // Stop trying after 5 failed attempts
      reconnectionDelay: 2000,
    });
    queueSocketRef.current = queueSocket;

    queueSocket.on('connect', () => {
    
      queueSocket.emit('user_waiting', { userCode: currentUserCode, expertCode: expertCode });
    });

    queueSocket.on('connect_error', (err) => {
      console.error('❌ [ThreeButtonWrapper] Queue socket error:', err.message);
    });

    queueSocket.on('queue_position_update', (data: { position: number, isPremium: boolean }) => {
    
      dispatch(setQueue({ expertCode, expertName, queueRank: data.position, mode, intakeData }));
    });

    queueSocket.on('expert_ready', async (data) => {
      if (data.expertCode === expertCode && !isConnectingRef.current) {
        isConnectingRef.current = true;

        queueSocket.disconnect();
        queueSocketRef.current = null;

        // Show a brief non-blocking toast so user knows what's happening
        toast.info('Expert is ready! Connecting you now...', { duration: 3000 });

        try {
          // 🟢 FIX 2: Await the response BEFORE wiping the UI state out of Redux
          const retryResult = await routeSessionStart(mode, intakeData);
          if (retryResult?.sessionId) {
            dispatch(clearQueue());
            setIntakeMode(null);
            navigateSession(retryResult, mode);
            // Expert side rings automatically via ping_expert inside useChatRoom → joinSession
          } else {
            // Session start failed — let them try manually
            toast.error('Could not connect automatically. Please try again.');
          }
        } catch (err) {
          toast.error('Connection failed. Please try again.');
        } finally {
          isConnectingRef.current = false;
        }
      }
    });
  };

  const handleCancelQueue = () => {
  
    if (queueSocketRef.current) {
      queueSocketRef.current.disconnect();
      queueSocketRef.current = null;
    }
    connectivityAPI.leaveQueue(expertCode).catch(() => {});
    dispatch(clearQueue());
    setIntakeMode(null);
  };

  const handleMinimize = () => {
  
    dispatch(setMinimized(true));
  };

const precheckAndOpenIntake = async (
  mode: 'chat' | 'call' | 'video',
) => {

  // 🔥 Prevent spam click
  if (isConnectingRef.current) return;
  isConnectingRef.current = true;

  try {
    const result = await connectivityAPI.precheck({
      expertCode,
      sessionType: mode,
      categoryId: categoryId ?? null,
    });

    if (!result.canStart && !result.isPromoAllowed) {
      toast.error(result.message || 'Insufficient balance');

      setTimeout(() => {
        toast.info('Redirecting to Wallet...');
      }, 500);

      setTimeout(() => {
        navigate('/user/wallet');
      }, 3000);

      return;
    }

    if (result.isPromoAllowed) {
  const minutes = Math.floor(result.promoDuration / 60);

toast.success(`🔥 First ${minutes} min FREE consultation`);
    }

    setIntakeMode(mode);

  } catch (error: any) {
    console.error('Precheck Error:', error);

    if (error?.response?.status === 401) {
      toast.info('Please login first');
      setTimeout(() => navigate('/user-signup'), 3000);
      return;
    }

    if (!error?.response) {
      toast.error('Network error');
      return;
    }

    toast.error(
      error?.response?.data?.message ||
      'Unable to verify wallet balance'
    );

  } finally {
    // 🔥 MUST RESET
    isConnectingRef.current = false;
  }
};

  const handleChannelClick = ( proceed: () => void) => {
    // 🟢 Safe string match with a fallback to prevent undefined lowercase crashes
    const currentCategory = expertCategory?.toLowerCase() || "";

    if (currentCategory === 'doctor') {
      window.alert(
        "For rashes, infections, or injuries, we recommend Video Consultation for a more accurate diagnosis. " +
        "First-time users are also encouraged to use Video Mode for the best experience."
      );
    }
    proceed();
  };

  const handleReadyToConnect = async (intakeData: any, mode: 'chat' | 'call' | 'video') => {
    const result = await routeSessionStart(mode, intakeData);
    
    if (result?.isQueued) {
     
      dispatch(setQueue({ expertCode, expertName, queueRank: result.queueRank, mode, intakeData }));
      return;
    }

    if (result?.sessionId) {
    
      dispatch(clearQueue());
      setIntakeMode(null); 
      navigateSession(result, mode);
    }
  };

  const navigateSession = (result: any, mode: 'chat' | 'call' | 'video') => {
    
    // 🟢 1. CREATE THE UNIFIED SESSION OBJECT
    const sessionData = { 
      sessionId: result.sessionId, 
      type: mode, 
      expertCode: expertCode,
      rtcToken: result.rtcToken || null,
      channelName: result.channelName || null
    };

    // 🟢 2. DISPATCH TO REDUX SO NAVBAR KNOWS ABOUT IT INSTANTLY
   
    dispatch(setActiveSession(sessionData));

    // 🟢 3. ADD EXTRA UI METADATA FOR THE ROOM COMPONENTS
    const navigationState = { 
      ...sessionData,
      isInitiator: true,
      expertName: expertName,
      expertPhoto: expertPhoto || null,
    };
    
    
    
    if (mode === 'chat') {
      navigate(`/chat/${expertCode}`, { state: navigationState });
    } else if (mode === 'call') {
      navigate(`/call/${expertCode}`, { state: navigationState });
    } else if (mode === 'video') {
      navigate(`/video/${expertCode}`, { state: navigationState });
    }
  };

  const shouldShowModal = (intakeMode !== null && !isMyQueue) || (isMyQueue && !isMinimized);
  const currentMode = isMyQueue && activeQueue ? activeQueue.mode : intakeMode;

  return (
    <>
      {shouldShowModal && currentMode && createPortal(
        <ConnectivityIntakeModal 
          isOpen={true} 
          onClose={() => setIntakeMode(null)}
          expertName={expertName}
          categoryId={categoryId} // 🟢 Passes it right into the Modal!
          expertCategory={expertCategory}            
          expertSubCategories={expertSubCategories}   
          mode={currentMode}
          onReady={handleReadyToConnect}
          queueRank={isMyQueue && activeQueue ? activeQueue.queueRank : null}
          onCancelQueue={handleCancelQueue}
          onMinimize={handleMinimize} 
        />,
        document.body
      )}

      <div className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-4" onClick={(e) => e.stopPropagation()}>
        <ChatButton 
          variant={variant} 
          isAvailable={!isOffline && isChatAvailable} 
          isLive={isLive}
          onClick={() => 
            !isOffline && 
            isChatAvailable && 
         handleChannelClick(() => precheckAndOpenIntake('chat'))
          } 
        />
        <CallButton 
          variant={variant} 
          isAvailable={!isOffline && isCallAvailable} 
          isLive={isLive}
          onClick={() => 
            !isOffline && 
            isCallAvailable && 
          handleChannelClick(() => precheckAndOpenIntake('call'))
          } 
        />
        <VideoButton 
          variant={variant} 
          isAvailable={!isOffline && isVideoAvailable} 
          isLive={isLive}
          onClick={() => 
            !isOffline && 
            isVideoAvailable && 
           handleChannelClick(() => precheckAndOpenIntake('video'))
          } 
        />
      </div>
    </>
  );
};